"use client"

import React, { useEffect, useState } from "react"

import "./index.scss"

import { createFromIconfontCN } from "@ant-design/icons"

interface IconFontProps {
  type: string
  className?: string
  style?: React.CSSProperties
  onClick?: () => void
  /** iconfont图标将严格按照此宽度来占据布局空间，如不传，则按照fontSize来计算布局空间 */
  width?: number | string
  /** iconfont图标将严格按照此高度来占据布局空间，如不传，则按照fontSize来计算布局空间 */
  height?: number | string
  /** 降级显示内容：当脚本未加载时显示的备用内容 */
  fallback?: React.ReactNode
}

// iconfont 脚本 URL
const ICONFONT_SCRIPT_URL = "https://imgqn.smm.cn/static/metal-monorope/iconfont/iconfont_v3.4.js"

// 创建 IconFont 组件实例
// createFromIconfontCN 内部会处理脚本加载，避免重复加载
const IconFont1 = createFromIconfontCN({
  scriptUrl: ICONFONT_SCRIPT_URL,
})

/**
 * 检查 iconfont 脚本是否已加载
 * iconfont 脚本加载后会在 body 中插入一个隐藏的 SVG，包含所有 symbol
 */
const isScriptLoaded = (): boolean => {
  if (typeof window === "undefined" || typeof document === "undefined") return false
  const svgSymbols = document.querySelector("svg[style*='position: absolute'][style*='width: 0']")
  return Boolean(svgSymbols?.querySelector("symbol"))
}

type IconfontScriptStatus = "checking" | "loaded" | "failed"

// 模块级探测状态：无论页面渲染多少个 IconFont，都只创建一套 observer 和兜底定时器。
let globalScriptStatus: IconfontScriptStatus = "checking"
let detectionStarted = false
const loadListeners = new Set<(status: IconfontScriptStatus) => void>()

/** 通知所有订阅者探测已结束。 */
const notifyScriptStatus = (status: IconfontScriptStatus) => {
  if (globalScriptStatus === status) return
  globalScriptStatus = status
  loadListeners.forEach((listener) => listener(status))
  loadListeners.clear()
}

/**
 * 启动唯一一套 iconfont 脚本探测。
 * 探测超时时仍保留 IconFont，以便慢网络下脚本稍后完成时正常显示。
 */
const startIconfontScriptDetection = () => {
  if (detectionStarted || typeof window === "undefined" || typeof document === "undefined") return
  detectionStarted = true

  if (isScriptLoaded()) {
    notifyScriptStatus("loaded")
    return
  }

  const scriptElement = Array.from(document.scripts).find(
    (script) => script.dataset.namespace === ICONFONT_SCRIPT_URL || script.src === ICONFONT_SCRIPT_URL
  )

  const cleanup = () => {
    observer.disconnect()
    clearTimeout(timeoutId)
    scriptElement?.removeEventListener("load", checkScript)
    scriptElement?.removeEventListener("error", handleScriptError)
  }

  const finish = (status: Exclude<IconfontScriptStatus, "checking">) => {
    if (globalScriptStatus !== "checking") return
    cleanup()
    notifyScriptStatus(status)
  }

  const checkScript = () => {
    if (!isScriptLoaded()) return false
    finish("loaded")
    return true
  }

  function handleScriptError() {
    finish("failed")
  }

  // symbol 必然通过 DOM 变更写入；一个 body 级 observer 即可覆盖所有 IconFont 实例。
  const observer = new MutationObserver(checkScript)

  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  })
  scriptElement?.addEventListener("load", checkScript)
  scriptElement?.addEventListener("error", handleScriptError)

  const timeoutId = setTimeout(() => {
    if (!checkScript()) {
      // 超时只停止探测，不移除 IconFont；慢网络下脚本稍后执行时图标仍能正常出现。
      cleanup()
      loadListeners.clear()
    }
  }, 2000)
}

/** 组件实例只订阅模块级状态，不再各自探测 DOM。 */
const useIconfontScript = (): IconfontScriptStatus => {
  const [status, setStatus] = useState<IconfontScriptStatus>(globalScriptStatus)

  useEffect(() => {
    startIconfontScriptDetection()
    setStatus(globalScriptStatus)
    if (globalScriptStatus !== "checking") return

    const listener = (nextStatus: IconfontScriptStatus) => setStatus(nextStatus)
    loadListeners.add(listener)
    return () => {
      loadListeners.delete(listener)
    }
  }, [])

  return status
}

const IconFont = ({ type, className, width, height, style, fallback, ...resetProps }: IconFontProps) => {
  const scriptStatus = useIconfontScript()

  // @deprecated width 和 height 已废弃，请使用 style.fontSize 替代
  const iconStyle = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
    ...style,
  }

  const wrapperStyle: React.CSSProperties = {
    ...iconStyle,
    width: iconStyle.width || iconStyle.fontSize || "auto",
    height: iconStyle.height || iconStyle.fontSize || "auto",
  }

  // 仅在探测明确失败后降级；探测期间正常渲染，以触发 iconfont 脚本注入。
  if (scriptStatus === "failed" && fallback) {
    return (
      <div className={`icon-wrapper icon-fallback ${className || ""}`} style={wrapperStyle}>
        {fallback}
      </div>
    )
  }

  // 脚本未加载且未提供降级内容：显示占位符（保持布局空间）
  if (scriptStatus === "failed") {
    return (
      <div className={`icon-wrapper icon-placeholder ${className || ""}`} style={wrapperStyle}>
        <span style={{ opacity: 0 }}>□</span>
      </div>
    )
  }

  // 正在检测或脚本已加载：正常渲染
  return <IconFont1 type={type} {...resetProps} className={`icon-wrapper ${className || ""}`} style={wrapperStyle} />
}

export default IconFont
export type { IconFontProps }
